import React, { useEffect, useMemo, useState } from "react";
import CustomizedSingleSelectDropdown from "../CreateForm/DropdownBase";

import _, { cloneDeep } from "lodash";
import {
  generateKeyList,
  generateRedirectFlowList,
} from "../CreateForm/DialogBase/logicUtils";
import { SpaceWrapper } from "../CreateForm/DialogBase/styled";

import {
  LogicAddConditionButton,
  LogicAddRuleButton,
  LogicDeleteConditionButton,
  LogicDeleteRuleButton,
  LogicRuleButton,
} from "../Custom/Buttons";
import FlowChangeConfirmationModal from "../Custom/Modals/FlowChangeConfirmationModal";
import { ErrorMessageContainer } from "./styled";

const ConditionDataProvider = [{ label: "If", value: "if" }];

const AddCondtionProvider = [
  { label: "or", value: "or" },
  { label: "and", value: "and" },
];

const LoopingDataProvider = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

function FlowBuilderSidebar({
  goToIndex,
  setGoToIndex,
  nodes,
  selectedNodeId,
  selectedNodeData,
  setFormDirty,
  allQuestionFormData,
  setNewQuestionData,
  newQuestionData,
  nextSelectedIndex,
}: any) {
  const [conditionList, setConditionList] = useState<any>([]);
  const [hasAddRuleEnabled, setHasAddRuleEnabled] = useState(false);
  const [isAddDisabled, setIsAddDisabled] = useState<boolean>(true);
  const [isLastNode, setIsLastName] = useState<boolean>(false);
  const [resetList, setResetList] = useState<boolean>(true);
  const [hasAddCondition, setAddConditionEnabled] = useState(false);
  const [conditionValue, setConditionValue] = useState("or");

  const [showPopup, setShowPopup] = useState(false);
  const [fieldValue, setFieldValue] = useState<any>({});

  const noOfCondition =
    ["dropdown", "multi-select", "select", "image-multi-select", "image-select"].includes(
      selectedNodeData?.questionData?.type
    ) && selectedNodeData?.questionData?.options?.length;

  useEffect(() => {
    setConditionList([]);
    setFieldValue({});

    setHasAddRuleEnabled(false);
    setResetList(true);
  }, [allQuestionFormData, selectedNodeId]);

  useEffect(() => {
    setIsLastName(false);
    if (!_.isEmpty(goToIndex) && goToIndex !== "noConnection") {
      goToIndex.forEach((goto: any) => {
        if (goto.nextIndex === "end") setIsLastName(true);
      });
    }
  }, [goToIndex, selectedNodeId]);

  useEffect(() => {
    let clonedData = cloneDeep(newQuestionData);
    setAddConditionEnabled(false);
    clonedData.forEach((question: any) => {
      if (
        question.qNo === selectedNodeData?.questionData.qNo &&
        question?.dependedType
      ) {
        setConditionValue(question?.dependedType);
        setAddConditionEnabled(true);
      }
    });
  }, [selectedNodeData, newQuestionData, conditionValue]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getLabel = (name: string) => {
    const foundNode = nodes.find((node: any) => node.id === name);

    if (foundNode && foundNode?.data && foundNode?.data?.label) {
      return foundNode?.data?.label;
    } else {
      return name;
    }
  };

  const optionDataProvider = useMemo(() => {
    const options = conditionList.map((item: any) => item.option);
    const newOptions = selectedNodeData.questionData.options.map(
      (item: any) => {
        if (options.includes(item.label)) {
          return { ...item, disabled: true, id: item?.id || item.name };
        }
        return { ...item, disabled: false, id: item?.id || item.name };
      }
    );
    return newOptions;
  }, [conditionList, selectedNodeData.questionData.options]);

  const fieldDataProvider = useMemo(
    () => (isLooping: boolean) => {
      const { redirectNondependentIndexList } =
        generateRedirectFlowList(newQuestionData);
      const nonDependedkeyList = Object.keys(redirectNondependentIndexList).map(
        (data) => Number(data)
      );

      const value = newQuestionData
        .filter((question: any, index: number) => {
          if (isLooping)
            return (
              nonDependedkeyList.includes(question.qNo) &&
              question.qNo < selectedNodeData.customNodeIndex
            );

          return question._id !== selectedNodeData.questionData._id;
        })
        .map((question: any) => {
          return {
            label: getLabel(question.name),
            value: question.name,
            disabled: false,
          };
        });

      value.push({
        label: "End",
        value: "end",
        disabled: isLooping ? true : false,
      });

      return value;
    },
    [
      getLabel,
      newQuestionData,
      selectedNodeData.customNodeIndex,
      selectedNodeData.questionData._id,
    ]
  );

  useEffect(() => {
    const elements = cloneDeep(conditionList);

    if (resetList) {
      if (
        ["dropdown", "multi-select", "select", "image-multi-select", "image-select"].includes(
          selectedNodeData.questionData.type
        )
      ) {
        selectedNodeData?.questionData.options.forEach((option: any) => {
          // eslint-disable-next-line array-callback-return
          const redirectNode = newQuestionData.filter((question: any) => {
            if (question.dependedId !== "") {
              if (typeof question.dependedId === "string") {
                return question.dependedId === option.id;
              } else {
                return question.dependedId.includes(option.id);
              }
            }
          });
          const redirectNodeList = redirectNode.map(
            (question: any, index: number) => {
              return question.name;
            }
          );

          const isLooping = redirectNodeList.includes(
            selectedNodeData?.questionData?.name
          );

          if (option.id && (redirectNode.length > 0 || option.isEnd)) {
            elements.push({
              condition: "if",
              option: option.value,
              field: option.isEnd
                ? ["end"]
                : isLooping
                  ? redirectNodeList[0]
                  : redirectNodeList,
              id: option.id,
              looping: isLooping ? "yes" : "no",
              isLooping: isLooping,
            });
          }
        });
        if (conditionList.length < noOfCondition) {
          setIsAddDisabled(false);
        }
        setConditionList(elements);
        setHasAddRuleEnabled(true);
        setResetList(false);
      } else {
        setHasAddRuleEnabled(false);
      }
    }
  }, [
    newQuestionData,
    conditionList,
    noOfCondition,
    resetList,
    selectedNodeData,
    selectedNodeId,
  ]);

  const addRuleHandler = () => {
    setHasAddRuleEnabled(true);
    addConditionHandler();
  };

  const addConditionHandler = () => {
    const elements = cloneDeep(conditionList);

    elements.push({
      condition: "if",
      option: null,
      field: [],
      id: null,
      looping: "no",
    });

    setIsAddDisabled(true);
    setConditionList(elements);
  };

  const modifyConditionHandler = (
    position: number,
    field: string,
    value: any
  ) => {
    const elements = cloneDeep(conditionList);

    elements.forEach((element: any, index: number) => {
      if (index === position) {
        const optionData = optionDataProvider.find(
          (option: any) => option.value === value
        );
        if (field === "option") {
          element.id = optionData.id
        }
        element[field] = value;
        let clonedData = cloneDeep(newQuestionData);
        if (field === "field") {
          clonedData = clonedData.map((clone: any, index: number) => {
            if (Array.isArray(value) && value.includes(clone.name)) {
              if (!clone["dependedId"]) {
                clone["dependedId"] = []
              } else if (!Array.isArray(clone["dependedId"])) {
                clone["dependedId"] = [clone["dependedId"]]
              }
              clone["isDepended"] = true;
              if (!clone["dependedId"].includes(element.id)) {
                clone["dependedId"].push(element.id)
              }
            } else if (value.length === 1 && value.includes("end")) {
              const currentIndex = clone?.options?.filter((cloneOption: any) => { if (cloneOption.id === element?.id) { return clone } })
              if (currentIndex?.length) {

                // return clone
              }
              // if(element?.id)
            }
            return clone
          })
        }


        // if (field === "looping") {
        //   element.looping = value;
        //   if (value === "yes") {
        //     element.field = "";
        //     element.isLooping = true;
        //   } else {
        //     element.field = [element?.field];
        //     element.isLooping = false;
        //   }
        //   setFormDirty(false);
        // }
        // if (
        //   !_.isEmpty(element?.field) &&
        //   element?.condition !== null &&
        //   element?.option !== null &&
        //   element.id !== null
        // ) {
        //   const { redirectNondependentIndexList, redirectDependentIndexList } =
        //     generateRedirectFlowList(newQuestionData);

        //   let clonedData = cloneDeep(newQuestionData);
        //   const oldData = conditionList[position];
        //   const newData = elements[position];

        //   clonedData.forEach((question: any, index: number) => {
        //     if (question.name === selectedNodeData.questionData.name) {
        //       if (
        //         ["dropdown", "multi-select", "select", "image-multi-select", "image-select"].includes(question.type)
        //       ) {
        //         const updatedOptionsList = clonedData[index].options.map(
        //           (option: any) => {
        //             let updatedOption = option;
        //             updatedOption = {
        //               ...option,
        //               id: option.id,
        //               isEnd: option.isEnd || false,
        //             };
        //             return updatedOption;
        //           }
        //         );
        //         clonedData[index].options = updatedOptionsList;
        //       }
        //     }
        //   });
        //   if (Array.isArray(oldData.field) && oldData.field.includes("end")) {
        //     clonedData.forEach((question: any, index: number) => {
        //       if (index === selectedNodeId) {
        //         const updatedOptionsList = clonedData[index].options.map(
        //           (option: any) => {
        //             let updatedOption = option;
        //             if (oldData.id === option.id)
        //               updatedOption = {
        //                 ...option,
        //                 id: option.id,
        //                 isEnd: false,
        //               };
        //             return updatedOption;
        //           }
        //         );
        //         clonedData[index].options = updatedOptionsList;
        //       }
        //     });

        //     const endArray = endIndexArray[oldData.id];
        //     if (!_.isEmpty(endArray)) {
        //       clonedData.forEach((question: any, index: number) => {
        //         if (endArray.includes(index)) {
        //           clonedData[index].isDepended = false;
        //         }
        //       });
        //     }
        //   } else if (
        //     typeof oldData.field === "string"
        //       ? oldData.field !== ""
        //       : !_.isEmpty(oldData.field)
        //   ) {
        //     clonedData = updateConditionRuleRecursive(
        //       Number(selectedNodeId),
        //       clonedData,
        //       redirectNondependentIndexList,
        //       redirectDependentIndexList,
        //       oldData
        //     );
        //   }
        //   if (Array.isArray(newData.field) && newData.field.includes("end")) {
        //     clonedData.forEach((question: any, index: number) => {
        //       if (index === selectedNodeId) {
        //         const updatedOptionsList = clonedData[index].options.map(
        //           (option: any) => {
        //             let updatedOption = option;
        //             if (newData.id === option.id)
        //               updatedOption = {
        //                 ...option,
        //                 id: option.id,
        //                 isEnd: true,
        //               };
        //             return updatedOption;
        //           }
        //         );
        //         clonedData[index].options = updatedOptionsList;
        //       }
        //     });

        //     const nonDependedkeyList = generateKeyList(clonedData, false);
        //     const dependedkeyList = generateKeyList(clonedData, true);

        //     let startIndex: any;
        //     if (!nonDependedkeyList.includes(selectedNodeId)) {
        //       startIndex = getParentIndex(
        //         redirectNondependentIndexList,
        //         redirectDependentIndexList,
        //         selectedNodeId,
        //         dependedkeyList
        //       );
        //     } else {
        //       startIndex = selectedNodeId;
        //     }
        //     const endArray: any = [];
        //     clonedData.forEach((question: any, index: number) => {
        //       if (
        //         index > startIndex &&
        //         question.name !== selectedNodeData.questionData.name &&
        //         nonDependedkeyList.includes(index)
        //       ) {
        //         clonedData[index].isDepended = true;
        //         const currentIndexOptionId = generateLabelList(
        //           clonedData[index]
        //         );
        //         if (!_.isEmpty(currentIndexOptionId)) {
        //           for (const key in currentIndexOptionId) {
        //             clonedData.forEach((ques: any, index: number) => {
        //               if (
        //                 Array.isArray(ques.dependedId) &&
        //                 ques.dependedId.includes(key)
        //               ) {
        //                 const updatedId = ques.dependedId.filter(
        //                   (id: string) => {
        //                     return id !== key;
        //                   }
        //                 );
        //                 if (updatedId.length > 1) {
        //                   ques.dependedId = updatedId;
        //                 } else {
        //                   ques.dependedId = updatedId[0];
        //                 }
        //               } else if (ques.dependedId === key) {
        //                 ques.dependedId = "";
        //               }
        //             });
        //           }
        //         }
        //         endArray.push(index);
        //       }
        //     });
        //     const obj = { [oldData.id]: endArray };
        //     setEndIndexArray({ ...endIndexArray, ...obj });
        //   } else {
        //     clonedData.forEach((question: any, index: number) => {
        //       if (
        //         typeof newData.field === "string"
        //           ? newData.field === question.name
        //           : newData.field.includes(question.name)
        //       ) {
        //         const nonDependedkeyList = generateKeyList(clonedData, false);

        //         const indexOfSelectedId = Number(selectedNodeId);
        //         const currentIndex = Number(index);
        //         const isLooping = newData.isLooping;
        //         if (isLooping) {
        //           const dependedkeyList = generateKeyList(clonedData, true);

        //           let startIndex: any;
        //           if (!nonDependedkeyList.includes(selectedNodeId)) {
        //             startIndex = getParentIndex(
        //               redirectNondependentIndexList,
        //               redirectDependentIndexList,
        //               selectedNodeId,
        //               dependedkeyList
        //             );
        //           } else {
        //             startIndex = selectedNodeId;
        //           }
        //           const updatedDependedkeyList = nonDependedkeyList;
        //           if (!nonDependedkeyList.includes(selectedNodeId))
        //             updatedDependedkeyList.push(indexOfSelectedId);
        //           updatedDependedkeyList?.forEach((quesIndex: number) => {
        //             if (
        //               (quesIndex <= startIndex ||
        //                 quesIndex === selectedNodeId) &&
        //               (nonDependedkeyList.includes(selectedNodeId)
        //                 ? quesIndex >= currentIndex
        //                 : true)
        //             ) {
        //               clonedData = modifyConditionRuleRecursive(
        //                 [quesIndex],
        //                 clonedData,
        //                 newData,
        //                 isLooping
        //               );
        //             }
        //           });
        //         } else {
        //           clonedData = modifyConditionRuleRecursive(
        //             [currentIndex],
        //             clonedData,
        //             newData,
        //             isLooping
        //           );
        //         }
        //       }
        //     });
        //   }
        setNewQuestionData(clonedData);

        //   setIsAddDisabled(false);
        // }
      }
    });

    setConditionList(elements);
  };

  const deleteConditionRuleRecursive = (
    selectedId: any,
    QuestionDetails: any,
    redirectNondependentIndexList: any,
    redirectDependentIndexList: any,
    removedDetails: any
  ) => {
    const clonedQuestionDetails = QuestionDetails;
    clonedQuestionDetails?.forEach((question: any, index: number) => {
      if (selectedId === index) {
        const currentDetails =
          redirectNondependentIndexList[index] ||
          redirectDependentIndexList[index];

        if (Object.keys(currentDetails).length > 0) {
          for (const key in currentDetails) {
            if (key === removedDetails?.id && currentDetails[key]) {
              const selectedArray = currentDetails[key];
              selectedArray.forEach((questionId: any) => {
                if (
                  typeof clonedQuestionDetails[questionId].dependedId ===
                  "string"
                ) {
                  clonedQuestionDetails[questionId].isDepended = false;
                  clonedQuestionDetails[questionId].dependedId = "";
                  if (clonedQuestionDetails[questionId]?.dependedType) {
                    let clonedQuestion = cloneDeep(
                      clonedQuestionDetails[questionId]
                    );
                    delete clonedQuestion["dependedType"];
                    clonedQuestionDetails[questionId] = clonedQuestion;
                  }
                } else {
                  const dependentIds = clonedQuestionDetails[
                    questionId
                  ].dependedId?.filter(
                    (dependedId: string) => dependedId !== removedDetails.id
                  );
                  if (dependentIds.length === 1) {
                    clonedQuestionDetails[questionId].dependedId =
                      dependentIds[0];
                  } else {
                    clonedQuestionDetails[questionId].dependedId = dependentIds;
                  }
                }
              });
            }
          }
        }
      }
    });

    return clonedQuestionDetails;
  };

  const deleteConditionHandler = (position: number) => {
    const newConditionList = conditionList.filter(
      (item: any, index: number) => index !== position
    );
    setConditionList(newConditionList);
    setIsAddDisabled(false);
    const removedDetails = conditionList.find(
      (item: any, index: number) => index === position
    );

    const { redirectNondependentIndexList, redirectDependentIndexList } =
      generateRedirectFlowList(newQuestionData);

    let clonedData = cloneDeep(newQuestionData);

    if (
      Array.isArray(removedDetails?.field) &&
      removedDetails?.field.includes("end")
    ) {
      clonedData.forEach((question: any, index: number) => {
        if (question.name === selectedNodeData.questionData.name) {
          const updatedOptionsList = clonedData[index].options.map(
            (option: any) => {
              let optionDetails = option;
              if (option.id === removedDetails.id)
                optionDetails["isEnd"] = false;
              return optionDetails;
            }
          );
          clonedData[index].options = updatedOptionsList;
        }
      });
    } else {
      if (
        typeof removedDetails.field === "string"
          ? removedDetails.field !== ""
          : !_.isEmpty(removedDetails.field)
      ) {
        clonedData = deleteConditionRuleRecursive(
          Number(selectedNodeId),
          clonedData,
          redirectNondependentIndexList,
          redirectDependentIndexList,
          removedDetails
        );
      }
    }

    if (newConditionList.length === 0) {
      clonedData.forEach((question: any, index: number) => {
        if (question.name === selectedNodeData.questionData.name) {
          const updatedOptionsList = clonedData[index].options.map(
            (option: any) => {
              let optionDetails = option;
              delete optionDetails["id"];
              return optionDetails;
            }
          );
          clonedData[index].options = updatedOptionsList;
        }
      });
    }

    setIsAddDisabled(false);
    setFormDirty(true);
    setNewQuestionData(clonedData);
  };

  const hasAdvancedRuleConfig = useMemo(() => {
    return (
      selectedNodeData &&
      selectedNodeData.questionData &&
      selectedNodeData.questionData.type &&
      ["dropdown", "multi-select", "select", "image-multi-select", "image-select"].includes(
        selectedNodeData.questionData.type
      )
    );
  }, [selectedNodeData]);

  const gotoDropdownDataProvider = useMemo(() => {
    const gotoOptions = nodes
      .filter(
        (node: any) =>
          node.customNodeIndex !== selectedNodeId && node.id !== "end"
      )
      .map((node: any) => {
        return {
          label: node.data.label,
          value: node.customNodeIndex,
        };
      });

    gotoOptions.push({ label: "End", value: "end" });

    return gotoOptions;
  }, [nodes, selectedNodeId]);

  const addCondition = (value: string) => {
    let clonedData = cloneDeep(newQuestionData);

    clonedData.forEach((question: any, index: number) => {
      if (question.qNo === selectedNodeData?.questionData.qNo) {
        setConditionValue(value);
        clonedData[index].dependedType = value;
      }
    });

    setFormDirty(true);
    setNewQuestionData(clonedData);
  };

  const deleteCondition = () => {
    let clonedData = cloneDeep(newQuestionData);
    setAddConditionEnabled(false);
    clonedData.forEach((question: any, index: number) => {
      if (question.qNo === selectedNodeData?.questionData.qNo) {
        let clonedQuestion = cloneDeep(question);
        delete clonedQuestion["dependedType"];
        clonedData[index] = clonedQuestion;
      }
    });

    setFormDirty(true);
    setNewQuestionData(clonedData);
  };

  const updateGotoIndex = (position: number, value: any) => {
    const gotIndexList = cloneDeep(goToIndex);

    gotIndexList.forEach((element: any, index: number) => {
      if (index === position) {
        element.nextIndex = value;
      }
    });
    setGoToIndex(gotIndexList);
  };

  const handlePopupConfirmation = () => {
    setShowPopup(false);
    modifyConditionHandler(fieldValue.index, "field", ["end"]);
    setFieldValue({});
  };

  const handleRedirectDropdownChange = () => {
    return (
      <FlowChangeConfirmationModal
        cancelText="No"
        contentText="Are you sure you want to select the 'end' option? The previously selected option will be deselected."
        submitText="Yes"
        isOpen={showPopup}
        cancelHandler={() => {
          setShowPopup(false);
        }}
        submitHandler={() => {
          handlePopupConfirmation();
        }}
      />
    );
  };

  const renderAddConditionSection = () => {
    return (
      <>
        {selectedNodeData?.questionData?.dependedId !== "" && (
          <SpaceWrapper>
            <LogicAddConditionButton
              disabled={hasAddCondition}
              onClick={() => setAddConditionEnabled(true)}
            />
          </SpaceWrapper>
        )}
        {hasAddCondition && (
          <SpaceWrapper>
            <SpaceWrapper>
              <CustomizedSingleSelectDropdown
                fullWidth
                dataProvider={AddCondtionProvider}
                value={conditionValue}
                setValue={(value) => {
                  addCondition(value);
                }}
              />
            </SpaceWrapper>
            <SpaceWrapper>
              <LogicDeleteConditionButton onClick={() => deleteCondition()} />
            </SpaceWrapper>
          </SpaceWrapper>
        )}
      </>
    );
  };

  const renderBasicRuleSection = () => {
    if (!_.isEmpty(goToIndex)) {
      return (
        <React.Fragment>
          {isLastNode && (
            <SpaceWrapper>It will redirect to the end page.</SpaceWrapper>
          )}
          <SpaceWrapper>
            <div>Always go to</div>
          </SpaceWrapper>
          <SpaceWrapper>
            <CustomizedSingleSelectDropdown
              fullWidth
              dataProvider={gotoDropdownDataProvider}
              value={goToIndex[0]?.nextIndex}
              setValue={(value) => {
                setFormDirty(false);
                updateGotoIndex(0, value);
              }}
            />
          </SpaceWrapper>
          {renderErrorMessage(goToIndex[0]?.nextIndex, 0)}
        </React.Fragment>
      );
    }
  };

  const renderErrorMessage = (nextIndex: any, index: number) => {
    const dependedkeyList = generateKeyList(newQuestionData, true);
    if (
      !(nextSelectedIndex[index]?.nextIndex === goToIndex[index]?.nextIndex)
    ) {
      if (
        dependedkeyList.includes(nextIndex) &&
        newQuestionData[nextIndex].dependedId !== ""
      ) {
        return (
          <SpaceWrapper>
            <ErrorMessageContainer>
              You can't select already depended question
            </ErrorMessageContainer>
          </SpaceWrapper>
        );
      }
    }
  };

  const renderAdvancedRuleSection = () => {
    if (hasAddRuleEnabled) {
      return (
        <React.Fragment>
          {hasAdvancedRuleConfig &&
            conditionList.map((item: any, index: number) => {
              return (
                <React.Fragment>
                  <SpaceWrapper>
                    <SpaceWrapper>Rule</SpaceWrapper>
                    <CustomizedSingleSelectDropdown
                      fullWidth
                      dataProvider={ConditionDataProvider}
                      value={item.condition}
                      setValue={(value) => {
                        modifyConditionHandler(index, "condition", value);
                      }}
                    />
                  </SpaceWrapper>
                  <SpaceWrapper>
                    <SpaceWrapper>Looping</SpaceWrapper>
                    <CustomizedSingleSelectDropdown
                      fullWidth
                      dataProvider={LoopingDataProvider}
                      value={item.looping}
                      setValue={(value) => {
                        modifyConditionHandler(index, "looping", value);
                      }}
                    />
                  </SpaceWrapper>
                  {item.condition === "if" && (
                    <SpaceWrapper>
                      <SpaceWrapper>Option</SpaceWrapper>
                      <CustomizedSingleSelectDropdown
                        fullWidth
                        dataProvider={optionDataProvider}
                        value={item.option}
                        setValue={(value) => {
                          modifyConditionHandler(index, "option", value);
                        }}
                      />
                    </SpaceWrapper>
                  )}
                  <SpaceWrapper>
                    <SpaceWrapper>Redirect to</SpaceWrapper>
                    <CustomizedSingleSelectDropdown
                      isMulti={!item.isLooping}
                      fullWidth
                      dataProvider={fieldDataProvider(item.isLooping)}
                      value={item.field}
                      setValue={(value) => {
                        if (typeof value === "string") {
                          modifyConditionHandler(index, "field", value);
                        } else if (
                          conditionList[index]?.field?.includes("end")
                        ) {
                          const filteredValue = value.filter((item: any) => {
                            return item !== "end";
                          });
                          modifyConditionHandler(index, "field", filteredValue);
                        } else if (value.includes("end") && value.length > 1) {
                          setShowPopup(true);
                          setFieldValue({ index, value });
                        } else {
                          setFormDirty(true);
                          modifyConditionHandler(index, "field", value);
                        }
                      }}
                    />
                  </SpaceWrapper>
                  <SpaceWrapper>
                    <LogicDeleteRuleButton
                      onClick={() => deleteConditionHandler(index)}
                    />
                  </SpaceWrapper>
                </React.Fragment>
              );
            })}
          {conditionList.length < noOfCondition && (
            <>
              <LogicRuleButton
                disabled={isAddDisabled}
                onClick={addConditionHandler}
              />
              <SpaceWrapper>
                {isLastNode && (
                  <SpaceWrapper>It will redirect to the end page.</SpaceWrapper>
                )}
              </SpaceWrapper>

              {!_.isEmpty(goToIndex || []) ? (
                goToIndex?.map((goto: any, index: number) => {
                  return (
                    <SpaceWrapper>
                      <SpaceWrapper>
                        {hasAdvancedRuleConfig && <div>Always go to</div>}
                      </SpaceWrapper>
                      <SpaceWrapper> If {goto?.optionName}</SpaceWrapper>
                      <CustomizedSingleSelectDropdown
                        fullWidth
                        dataProvider={gotoDropdownDataProvider}
                        value={goto?.nextIndex}
                        setValue={(value) => {
                          setFormDirty(false);
                          updateGotoIndex(index, value);
                        }}
                      />
                      {renderErrorMessage(goto.nextIndex, index)}
                    </SpaceWrapper>
                  );
                })
              ) : (
                <></>
              )}
            </>
          )}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        {renderBasicRuleSection()}
        <SpaceWrapper>
          <LogicAddRuleButton onClick={addRuleHandler} />
        </SpaceWrapper>
      </React.Fragment>
    );
  };

  return goToIndex === "noConnection" ? (
    <SpaceWrapper>
      Current question's parent or current question has no connection to any of
      the questions, please try adding current question to any of the other
      questions
    </SpaceWrapper>
  ) : (
    <>
      {hasAdvancedRuleConfig
        ? renderAdvancedRuleSection()
        : renderBasicRuleSection()}
      {renderAddConditionSection()}
      {handleRedirectDropdownChange()}
    </>
  );
}

export default FlowBuilderSidebar;
